import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { fetchWithAuth } from '../../../utils/api';
import { APIErrorException } from '../../../services/types/errors';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { NETWORK_FAILURE } from './V3UserForm';

interface EmailLinkUser {
  user_id: string;
  username: string;
  name?: string | null;
  email: string;
  is_active: boolean;
}

interface MagicLinkResponse {
  magic_link: string;
  email_sent?: boolean;
  email_to?: string | null;
}

interface V3EmailLinkDialogProps {
  user: EmailLinkUser;
  onClose: () => void;
}

type Phase =
  | { kind: 'confirm' }
  | { kind: 'sending' }
  | { kind: 'result'; link: string; sent: boolean; email: string }
  | { kind: 'inactive' }
  | { kind: 'failed'; message: string };

/** The greeting name the invitation email uses: the first word of the name, else the username. */
export const firstNameOf = (user: Pick<EmailLinkUser, 'name' | 'username'>): string =>
  user.name?.trim().split(/\s+/)[0] || user.username;

/**
 * Confirms, sends the sign-in link and says whether the email went, the link
 * always shown; an inactive person gets an explanation instead.
 */
const V3EmailLinkDialog: React.FC<V3EmailLinkDialogProps> = ({ user, onClose }) => {
  const [phase, setPhase] = useState<Phase>(user.is_active ? { kind: 'confirm' } : { kind: 'inactive' });
  const name = user.name || user.username;
  const sending = phase.kind === 'sending';

  const send = async () => {
    setPhase({ kind: 'sending' });
    try {
      const data: MagicLinkResponse = await fetchWithAuth(`/v1/auth/admin/users/${user.user_id}/magic-link`, {
        method: 'POST',
      });
      // A response without the email fields reads as not sent.
      setPhase({ kind: 'result', link: data.magic_link, sent: !!data.email_sent, email: data.email_to || user.email });
    } catch (err) {
      if (err instanceof APIErrorException && err.status === 409) {
        setPhase({ kind: 'inactive' });
      } else {
        setPhase({ kind: 'failed', message: err instanceof APIErrorException ? err.error.message : NETWORK_FAILURE });
      }
      console.error(err);
    }
  };

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied');
    } catch (err) {
      toast.error("Couldn't copy the link. Select it and copy it yourself.");
      console.error(err);
    }
  };

  const body = () => {
    switch (phase.kind) {
      case 'confirm':
      case 'sending':
        return (
          <>
            <DialogHeader>
              <DialogTitle>Email a sign-in link</DialogTitle>
              <DialogDescription>
                We'll email {name} at {user.email} a link to set a new password. It works for 24 hours.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
              <Button onClick={send} disabled={sending}>Send email</Button>
            </DialogFooter>
          </>
        );
      case 'result':
        return (
          <>
            <DialogHeader>
              <DialogTitle>Email a sign-in link</DialogTitle>
            </DialogHeader>
            {phase.sent ? (
              <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>Sent to {phase.email}.</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>The email to {phase.email} didn't send. Copy the link below and send it to them yourself.</span>
              </div>
            )}
            <div className="grid gap-2">
              <p className="text-sm font-medium">Or copy the link</p>
              <div className="flex gap-2">
                <Input readOnly value={phase.link} aria-label="Sign-in link" onFocus={(e) => e.target.select()} />
                <Button variant="outline" onClick={() => copy(phase.link)}>Copy</Button>
              </div>
              <p className="text-sm text-muted-foreground">The link works for 24 hours.</p>
            </div>
            <DialogFooter>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </>
        );
      case 'inactive':
        return (
          <>
            <DialogHeader>
              <DialogTitle>{name} is inactive</DialogTitle>
              <DialogDescription>
                Inactive people can't sign in, so we can't send them a link. Edit {firstNameOf(user)}'s account and
                tick Active account first.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        );
      case 'failed':
        return (
          <>
            <DialogHeader>
              <DialogTitle>Email a sign-in link</DialogTitle>
              <DialogDescription role="alert">{phase.message}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !sending) onClose(); }}>
      {/* Every phase has its own one way out, so the corner close is not drawn. The
          result has no description; the other phases link theirs automatically. */}
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        {...(phase.kind === 'result' ? { 'aria-describedby': undefined } : {})}
      >
        {body()}
        {/* Mounted in every phase, so the outcome is announced when it arrives. */}
        <p className="sr-only" role="status">
          {phase.kind === 'result'
            ? phase.sent
              ? `Sent to ${phase.email}.`
              : `The email to ${phase.email} didn't send.`
            : ''}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default V3EmailLinkDialog;
